/**
 * Document manifests for each exchange hub.
 * Defines which documents belong to each phase of the 1031 exchange.
 */

export interface HubDocument {
  name: string
  required: boolean
  actionType: 'upload' | 'e-sign' | 'auto-generate' | 'review'
  description: string
  category: 'documents' | 'irs' | 'property' | 'compliance'
}

export const SALES_HUB_DOCUMENTS: HubDocument[] = [
  {
    name: 'Exchange Agreement',
    required: true,
    actionType: 'e-sign',
    description: 'QI exchange agreement between exchanger and qualified intermediary',
    category: 'documents',
  },
  {
    name: 'W-9 Tax ID Certification',
    required: true,
    actionType: 'e-sign',
    description: 'IRS Form W-9 for tax identification verification',
    category: 'irs',
  },
  {
    name: 'Assignment of Relinquished Property Contract',
    required: true,
    actionType: 'review',
    description: 'Assigns the sale contract to the QI for exchange purposes',
    category: 'documents',
  },
  {
    name: 'Notice of Assignment (Buyer)',
    required: true,
    actionType: 'review',
    description: 'Notifies the buyer that the contract has been assigned to the QI',
    category: 'documents',
  },
  {
    name: 'Purchase/Sale Agreement (Relinquished)',
    required: true,
    actionType: 'upload',
    description: 'The executed purchase and sale agreement for the relinquished property',
    category: 'property',
  },
  {
    name: 'Preliminary Title Report',
    required: false,
    actionType: 'upload',
    description: 'Title report for the relinquished property (required if in escrow)',
    category: 'property',
  },
]

export const IDENTIFICATION_HUB_DOCUMENTS: HubDocument[] = [
  {
    name: 'Identification of Replacement Property Form',
    required: true,
    actionType: 'e-sign',
    description: 'Formal identification of replacement properties within the 45-day window',
    category: 'compliance',
  },
  {
    name: 'Settlement Statement (Relinquished - HUD-1)',
    required: true,
    actionType: 'upload',
    description: 'Final settlement/closing statement for the relinquished property sale',
    category: 'documents',
  },
]

export const PURCHASE_HUB_DOCUMENTS: HubDocument[] = [
  {
    name: 'Purchase Agreement (Replacement)',
    required: true,
    actionType: 'upload',
    description: 'Executed purchase agreement for the replacement property',
    category: 'property',
  },
  {
    name: 'Assignment of Replacement Property Contract',
    required: true,
    actionType: 'e-sign',
    description: 'Assigns the purchase contract to the QI for exchange purposes',
    category: 'documents',
  },
  {
    name: 'Notice of Assignment (Seller)',
    required: true,
    actionType: 'e-sign',
    description: 'Notifies the seller that the contract has been assigned to the QI',
    category: 'documents',
  },
  {
    name: 'Settlement Statement (Replacement - HUD-1)',
    required: true,
    actionType: 'upload',
    description: 'Final settlement/closing statement for the replacement property purchase',
    category: 'documents',
  },
]

export const DISBURSEMENT_HUB_DOCUMENTS: HubDocument[] = [
  {
    name: 'Disbursement Authorization',
    required: true,
    actionType: 'e-sign',
    description: 'Exchanger authorization directing the QI to release qualified escrow funds to the replacement-property closing company',
    category: 'documents',
  },
  {
    name: 'Wire Transfer Confirmation',
    required: true,
    actionType: 'upload',
    description: 'Bank-issued wire confirmation receipt confirming funds were transmitted to the replacement-property closing company',
    category: 'documents',
  },
  {
    name: 'Exchange Closing Statement (Relinquished)',
    required: true,
    actionType: 'auto-generate',
    description: 'QI-generated closing statement for the relinquished property leg',
    category: 'irs',
  },
  {
    name: 'Exchange Closing Statement (Replacement)',
    required: true,
    actionType: 'auto-generate',
    description: 'QI-generated closing statement for the replacement property leg',
    category: 'irs',
  },
  {
    name: 'Exchange Transaction Report',
    required: true,
    actionType: 'auto-generate',
    description: 'Complete transaction report summarizing the entire exchange',
    category: 'irs',
  },
  {
    name: 'IRS Form 8824 (Like-Kind Exchanges)',
    required: true,
    actionType: 'auto-generate',
    description: 'Pre-populated IRS Form 8824 for tax filing',
    category: 'irs',
  },
]

export const ALL_HUB_DOCUMENTS = {
  sales: SALES_HUB_DOCUMENTS,
  identification: IDENTIFICATION_HUB_DOCUMENTS,
  purchase: PURCHASE_HUB_DOCUMENTS,
  disbursement: DISBURSEMENT_HUB_DOCUMENTS,
}

export type HubType = keyof typeof ALL_HUB_DOCUMENTS
